import { useEffect, useMemo, useState } from "react";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import {
  extractProjectDetailPayload,
  mapProjectToDetailView,
} from "lib/projects";

/** Loads a single project for the campaign detail page. */
export function useCampaignProject(id) {
  const [rawProject, setRawProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Missing project id");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get(API_ENDPOINTS.projectDetail(id), { auth: false });
        if (cancelled) return;
        if (!isSuccessResponse(resp)) {
          setRawProject(null);
          setError(resp?.message || "Could not load this project");
          return;
        }
        const p = extractProjectDetailPayload(resp);
        if (!p || typeof p !== "object") {
          setRawProject(null);
          setError("Invalid project response");
          return;
        }
        setRawProject(p);
      } catch (e) {
        if (!cancelled) {
          setRawProject(null);
          setError(e?.message || "Could not load this project");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const campaign = useMemo(
    () => (rawProject ? mapProjectToDetailView(rawProject) : null),
    [rawProject]
  );

  return { campaign, loading, error };
}
